/**
 * Admin Settings page (plan `playbooks-clay-wire-gaps`, Unit 3) — pure,
 * DOM/React-free logic behind `components/PlaybooksSettingsPage.tsx`.
 *
 * Split out for the same reason `config-page-logic.ts` is: the page
 * component pulls in Glaze components (`InputField`, `Button`, `useAlertDialog`,
 * …) whose modules crash at import time under `bun:test` — there is no DOM
 * (`mini-apps/CLAUDE.md`'s "no DOM harness" convention). This leaf has zero
 * Glaze/React imports, so both the component (which imports these directly)
 * and `__tests__/settings-page-logic.test.ts` can import it safely.
 */
import { MiniAppFetchError } from '../_shared/fetch-error';
import type { PlaybooksPlaysLibraryGroup, PlaybooksPlaySummary } from './schemas';

/**
 * The status line the Settings page shows above the routine-id field —
 * "what will happen to the next send" in plain language, mirroring
 * `settings.ts#resolvePlaybooksClayRoutineId`'s own null-is-webhook contract
 * exactly: `null` means the webhook transport, anything else names the Clay
 * routine run.
 */
export function describeTransport(clayRoutineId: string | null): string {
  return clayRoutineId === null
    ? 'Webhook — routine id not set'
    : `Clay routine run — ${clayRoutineId}`;
}

/**
 * Normalizes the routine-id `InputField`'s raw draft value the same way the
 * server does (`schemas.ts#PlaybooksUpdateSettingsRequestSchema`,
 * `settings.ts#writePlaybooksSettings`): trim, and collapse a
 * blank/whitespace-only result to `null` rather than storing/sending an empty
 * string. Running this client-side before the PATCH body is built means the
 * client and server never disagree about what counts as "cleared".
 */
export function normalizeRoutineIdInput(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Whether the Save button should be enabled — `saved` is the last value the
 * query returned (`data?.clayRoutineId ?? null`), `draft` is the CURRENT
 * field value ALREADY run through {@link normalizeRoutineIdInput} by the
 * caller (so `''`-vs-`null` never reads as a change: both normalize to
 * `null` before reaching here, matching the server's own "blank collapses to
 * null" contract). A plain `!==` is correct once both sides are normalized —
 * there is no other value in this domain (a string or `null`) that could
 * compare unequal while meaning the same thing.
 */
export function hasSettingsChange(saved: string | null, draft: string | null): boolean {
  return saved !== draft;
}

// ─────────────────────────────────────────────────────────────────────────────
// "Launched plays" admin section (plan `playbooks-clay-wire-gaps`, U5b)
// ─────────────────────────────────────────────────────────────────────────────

/** One row of the Settings page's "Launched plays" table — a play plus the campaign name its group carries (`null` for a campaign the mirror no longer has). */
export interface LaunchedPlayRow {
  play: PlaybooksPlaySummary;
  campaignName: string | null;
}

/**
 * Which plays the Settings page's "Launched plays" section shows: every
 * `status === 'launched'`, non-archived play across every campaign group from
 * `GET /plays/library` (`usePlaysLibrary`), most-recently-updated first.
 *
 * `GET /plays/library` already excludes archived plays entirely (its own doc
 * comment: "every non-archived play in the workspace"), so the `archivedAt`
 * check here is belt-and-suspenders, same reasoning as `filterPlaysLibrary`'s
 * `campaignName` null-check elsewhere in this app — cheap, and correct even
 * if that upstream contract ever loosens.
 *
 * Sorted by `updatedAt` DESCENDING (not `createdAt`): `updated_at` is a plain
 * Prisma `@updatedAt` column on `playbooks_play`, so it bumps the instant
 * `send.ts` flips a play to `'launched'` — the natural "when did this last
 * become launched/get touched" ordering an admin scanning for a stuck send
 * wants, and the same column `resetPlayEndpoint`'s own reset touches, so a
 * just-reset play (now `'draft'`, and gone from this list) can never confuse
 * the ordering of what remains.
 */
export function launchedPlayRows(
  groups: readonly PlaybooksPlaysLibraryGroup[],
): LaunchedPlayRow[] {
  const rows: LaunchedPlayRow[] = [];
  for (const group of groups) {
    for (const play of group.plays) {
      if (play.status !== 'launched') continue;
      if (play.archivedAt !== null) continue;
      rows.push({ play, campaignName: group.campaignName });
    }
  }
  return rows.sort((a, b) => b.play.updatedAt.localeCompare(a.play.updatedAt));
}

/**
 * "Wipe log" confirm copy — plain English for an admin who is not the rep who
 * ran this play. Says exactly what gets deleted (the per-member delivery log
 * `launcher_dispatch_log` rows) and, load-bearing, what does NOT: the play's
 * own send history (`playbooks_play_send`) is kept, so this can never read as
 * "undo the send" — that is `resetPlayWarning`'s button, not this one.
 */
export function wipeLogWarning(displayName: string): string {
  return (
    `Deletes the per-member delivery log for "${displayName}" — every row of who it tried to reach and ` +
    "what happened. This play's own send history is kept; this only clears the detailed log. " +
    'This cannot be undone.'
  );
}

/**
 * "Reset to draft" confirm copy. Two things an admin must know before
 * confirming, per the plan: the send history is kept (nothing about who was
 * already sent to is lost), and the play goes back to counting as "unsent" —
 * `usage.ts#isUnsentPlay`'s `status !== 'launched'` — so it becomes eligible
 * to re-launch.
 */
export function resetPlayWarning(displayName: string): string {
  return (
    `Puts "${displayName}" back into draft so it can be re-launched. Its send history is kept — ` +
    'this only undoes the launched status, and the play counts as unsent again.'
  );
}

/**
 * Row-action error copy for "Wipe log" / "Reset to draft" (Codex review
 * finding F3, playbooks-clay-wire-gaps plan): NEVER show a rep/admin a raw
 * status code (repo-wide CLAUDE.md rule). The row-action `catch` handlers
 * used to toast `err instanceof Error ? err.message : …` — but
 * `MiniAppFetchError#message` (`_shared/fetch-error.ts`) used to fall back to
 * `` `${code}:${status}` `` (e.g. `"http_502:502"`) exactly when the server's
 * `problem()` body carried no `detail`/`title`/`userMessage` (a non-JSON
 * body — an HTML error page from a proxy/CDN — or a JSON body missing all
 * three), which was precisely the raw-status-code shape the rule forbids.
 * Plan 2026-09-28-008 U4 closed that at the source too (the fallback is now
 * a generic sentence, never `${code}:${status}`) — this function stays as a
 * second, independent line of defense: it never reads `.message` at all.
 *
 * Prefers the server's structured `userMessage` (already resolved by
 * `throwFetchError`'s `detail` → `title` fallback chain), otherwise the
 * caller's own action-specific plain-English fallback — never `.message`.
 */
export function rowActionErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof MiniAppFetchError && err.userMessage) return err.userMessage;
  return fallback;
}
